import { Component, inject } from '@angular/core';
import { MachineService } from 'orchestrator';
import { ListService } from '@abp/ng.core';
import {
  ConfirmationService,
} from '@abp/ng.theme.shared';
import {
  MachineCreateDto,
  MachineDto,
  MachineExcelDownloadDto,
  MachineUpdateDto,
} from 'orchestrator';
import { MACHINE_CONFIG, MACHINE_FIELDS } from './machine.config';
import { BaseHierarchicalCrudComponent, GenericForm, ZnHierarchicalViewComponent } from '@zendra/shared';
import { CommonModule } from '@angular/common';
import { FieldMapperHelper } from '@zendra/shared';

@Component({
  selector: 'app-machine',
  imports: [
    CommonModule,
    GenericForm,
    ZnHierarchicalViewComponent,
  ],
  providers: [ListService, MachineService, ConfirmationService],
  templateUrl: './machine.html',
  styleUrl: './machine.scss',
})
export class Machine extends BaseHierarchicalCrudComponent<
  MachineDto,
  MachineCreateDto,
  MachineUpdateDto,
  MachineExcelDownloadDto
> {
  service = inject(MachineService);
  config = MACHINE_CONFIG;
  fields = MACHINE_FIELDS;

  private gridHookIntervalId: any;
  private gridHooked = false;

  constructor() {
    super();

    const fkDef = this.fields.find(f => f.key === 'machineTypeId') as any;
    if (fkDef?.column) {
      fkDef.column = {
        ...fkDef.column,
        editable: false,
        editor: undefined,
        cssClass: ((fkDef.column.cssClass ? fkDef.column.cssClass + ' ' : '') + 'read-only'),
      };

      fkDef.column.cssClass = (fkDef.column.cssClass ? fkDef.column.cssClass + ' ' : '') + 'read-only';
    }
  }

  override ngAfterViewInit(): void {
    super.ngAfterViewInit();

    this.gridHookIntervalId = setInterval(() => {
      if (this.gridHooked) {
        clearInterval(this.gridHookIntervalId);
        return;
      }

      const grid = (this as any).crudPage?.gridTable?.grid;
      if (!grid) {
        return;
      }

      const slick = grid.slickGrid;
      if (!slick?.onBeforeEditCell) {
        clearInterval(this.gridHookIntervalId);
        return;
      }

      slick.onBeforeEditCell.subscribe((_e: any, args: any) => {
        const field = args?.column?.field ?? args?.column?.id;

        if (field === 'machineTypeId') {
          return false;
        }
        return true;
      });

      this.gridHooked = true;
      clearInterval(this.gridHookIntervalId);
    }, 250);
  }

  override ngOnDestroy(): void {
    if (this.gridHookIntervalId) {
      clearInterval(this.gridHookIntervalId);
    }
    super.ngOnDestroy();
  }

  override edit(row: MachineDto): void {
    this.selectedEntity = { ...row };

    const fields = FieldMapperHelper.mapFieldsForForm(this.fields, row, this.injector).map(f => ({
      ...f,
      required: f.required,
      validators: f.validators
    }));

    const machineTypeField = fields.find(f => (f as any).key === 'machineTypeId') as any;
    if (machineTypeField) {
      machineTypeField['disabled'] = true;
      machineTypeField['readOnly'] = true;

      if (machineTypeField['fieldOptions']) {
        machineTypeField['fieldOptions'] = {
          ...machineTypeField['fieldOptions'],
          disabled: true,
          readOnly: true,
        };
      }

      if (machineTypeField['templateOptions']) {
        machineTypeField['templateOptions'] = {
          ...machineTypeField['templateOptions'],
          disabled: true,
          readonly: true,
        };
      }

      if (machineTypeField['formControl']?.disable) {
        machineTypeField['formControl'].disable({ emitEvent: false });
      }
    }

    this.genericForm.open({
      title: `${this.localizationResource.LanguageService}::EditRecord`,
      fields,
      entityLocaleKey: this.config.localizationKey
    });
  }

  protected prepareUpdateDto(data: any, selected: MachineDto): MachineUpdateDto {
    return {
      code: data.code,
      name: data.name,
      deactivationReasonId: data.deactivationReasonId,
      deactivationDate: data.deactivationDate,
      isActive: data.isActive,
      sapCode: data.sapCode,
      vendorId: data.vendorId,
      internalOrderId: data.internalOrderId,
      notes: data.notes,
      ureaCheck: data.ureaCheck,
      hasGps: data.hasGps,
      fabricationYear: data.fabricationYear,
      energySourceId: data.energySourceId,
      brandId: data.brandId,
      modelId: data.modelId,
      concurrencyStamp: selected.concurrencyStamp,
    };
  }

  protected getEntityName(): string {
    return 'Machine';
  }
}
